import { useEffect, useLayoutEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronRight, ChevronLeft, Sparkles } from "lucide-react";

// A single tour step targets a DOM element by data-testid and shows a glass
// popover next to it. The full page is dimmed except for a spotlight
// rectangle around the target.
//
// steps = [{ selector, title, body, placement?: 'top' | 'bottom' | 'left' | 'right' }]
// onFinish() fires on the last "Next" or when the user hits "Skip".

const GAP = 14; // px between spotlight and popover
const PADDING = 10; // px of visual breathing room around the highlighted target
const RADIUS = 14; // px corner radius on the spotlight

function useTargetRect(selector, tick) {
  const [rect, setRect] = useState(null);
  useLayoutEffect(() => {
    if (!selector) return;
    let raf = 0;
    const update = () => {
      const el = document.querySelector(selector);
      if (!el) {
        setRect(null);
        // Try again on the next frame in case the target hasn't mounted yet.
        raf = requestAnimationFrame(update);
        return;
      }
      // Bring the element into view for tall pages.
      const r = el.getBoundingClientRect();
      const inView = r.top >= 60 && r.bottom <= window.innerHeight - 60;
      if (!inView) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      // Read the rect again after scroll begins (final position settles a tick later).
      setRect(el.getBoundingClientRect());
    };
    update();
    const onWin = () => setRect(document.querySelector(selector)?.getBoundingClientRect() || null);
    window.addEventListener("resize", onWin);
    window.addEventListener("scroll", onWin, true);
    // Poll for a moment while smooth-scroll settles.
    const interval = setInterval(onWin, 120);
    setTimeout(() => clearInterval(interval), 900);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onWin);
      window.removeEventListener("scroll", onWin, true);
      clearInterval(interval);
    };
  }, [selector, tick]);
  return rect;
}

function computeCardPosition(rect, placement, cardW = 340, cardH = 200) {
  if (!rect) {
    // Centered fallback for missing targets.
    return {
      top: window.innerHeight / 2 - cardH / 2,
      left: window.innerWidth / 2 - cardW / 2,
      arrow: null,
    };
  }
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Auto: pick the side with the most room.
  let side = placement;
  if (!side) {
    const spaces = {
      bottom: vh - rect.bottom,
      top: rect.top,
      right: vw - rect.right,
      left: rect.left,
    };
    side = Object.entries(spaces).sort((a, b) => b[1] - a[1])[0][0];
  }

  let top = 0, left = 0;
  if (side === "bottom") { top = rect.bottom + GAP + PADDING; left = rect.left + rect.width / 2 - cardW / 2; }
  else if (side === "top") { top = rect.top - GAP - PADDING - cardH; left = rect.left + rect.width / 2 - cardW / 2; }
  else if (side === "right") { top = rect.top + rect.height / 2 - cardH / 2; left = rect.right + GAP + PADDING; }
  else { top = rect.top + rect.height / 2 - cardH / 2; left = rect.left - GAP - PADDING - cardW; }

  // Clamp inside the viewport with 16px margin.
  left = Math.max(16, Math.min(vw - cardW - 16, left));
  top = Math.max(16, Math.min(vh - cardH - 16, top));

  return { top, left, arrow: side };
}

export default function OnboardingTour({ steps = [], onFinish }) {
  const [i, setI] = useState(0);
  const [tick, setTick] = useState(0); // force target re-measure after step changes
  const step = steps[i];
  const cardRef = useRef(null);
  const [cardSize, setCardSize] = useState({ w: 340, h: 210 });

  const rect = useTargetRect(step?.selector, tick);

  useEffect(() => {
    // Bump tick when the current step changes so useTargetRect re-queries the DOM.
    setTick((t) => t + 1);
  }, [i]);

  useLayoutEffect(() => {
    if (cardRef.current) {
      const r = cardRef.current.getBoundingClientRect();
      setCardSize({ w: r.width, h: r.height });
    }
  }, [i, rect]);

  const finish = useCallback((completed) => {
    try { localStorage.removeItem("cs_just_carried_dish_id"); } catch {}
    onFinish?.(completed);
  }, [onFinish]);

  if (!step) return null;

  const isLast = i === steps.length - 1;
  const isFirst = i === 0;
  const { top, left, arrow } = computeCardPosition(rect, step.placement, cardSize.w, cardSize.h);

  // Spotlight uses a huge box-shadow to dim everything outside the target's rect.
  const spot = rect
    ? {
        top: rect.top - PADDING,
        left: rect.left - PADDING,
        width: rect.width + PADDING * 2,
        height: rect.height + PADDING * 2,
      }
    : null;

  const overlay = (
    <div
      aria-modal="true"
      role="dialog"
      className="fixed inset-0 z-[9999]"
      style={{ pointerEvents: "none" }}
      data-testid="onboarding-tour"
    >
      {/* Full-screen dim when we have no target, or use spotlight when we do */}
      {spot ? (
        <motion.div
          key={`spot-${i}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
          className="absolute rounded-[14px]"
          style={{
            top: spot.top,
            left: spot.left,
            width: spot.width,
            height: spot.height,
            borderRadius: RADIUS,
            boxShadow:
              "0 0 0 9999px rgba(0,0,0,0.68), inset 0 0 0 1.5px rgba(232,207,154,0.85), 0 0 0 5px rgba(232,207,154,0.16), 0 0 34px 4px rgba(232,207,154,0.28)",
            pointerEvents: "none",
          }}
        />
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/70"
        />
      )}

      {/* Glass popover card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={`card-${i}`}
          ref={cardRef}
          initial={{ opacity: 0, y: 8, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -6, scale: 0.98 }}
          transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
          className="absolute w-[340px] rounded-[20px] p-5"
          style={{
            top,
            left,
            pointerEvents: "auto",
            background:
              "linear-gradient(180deg, rgba(24,20,14,0.92), rgba(14,12,9,0.94))",
            border: "1px solid rgba(232,207,154,0.35)",
            boxShadow:
              "0 30px 70px -20px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.06), 0 0 0 1px rgba(232,207,154,0.08)",
            backdropFilter: "blur(18px) saturate(160%)",
            WebkitBackdropFilter: "blur(18px) saturate(160%)",
          }}
          data-testid={`onboarding-step-${i}`}
        >
          {/* Skip button */}
          <button
            onClick={() => finish(false)}
            className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center text-white/50 hover:text-white/90 hover:bg-white/5 transition-colors"
            aria-label="Skip tour"
            data-testid="onboarding-skip"
          >
            <X className="w-3.5 h-3.5" />
          </button>

          <div className="flex items-center gap-2 mb-3">
            <div
              className="w-6 h-6 rounded-full flex items-center justify-center"
              style={{
                background: "linear-gradient(135deg, rgba(232,207,154,0.35), rgba(200,169,106,0.08))",
                border: "1px solid rgba(232,207,154,0.4)",
              }}
            >
              <Sparkles className="w-3 h-3" style={{ color: "#f2dfb5" }} />
            </div>
            <div className="text-[0.55rem] uppercase tracking-[0.28em]" style={{ color: "rgba(232,207,154,0.85)" }}>
              Step {i + 1} / {steps.length}
            </div>
          </div>

          <h3 className="font-display text-xl text-white/95 leading-tight mb-2">{step.title}</h3>
          <p className="text-sm text-white/70 leading-relaxed mb-5">{step.body}</p>

          {/* Progress dots */}
          <div className="flex items-center gap-1.5 mb-4">
            {steps.map((_, idx) => (
              <span
                key={idx}
                className="h-1 rounded-full transition-all"
                style={{
                  width: idx === i ? 18 : 6,
                  background: idx <= i ? "rgba(232,207,154,0.9)" : "rgba(255,255,255,0.15)",
                }}
              />
            ))}
          </div>

          <div className="flex items-center justify-between gap-2">
            <button
              onClick={() => finish(false)}
              className="text-xs text-white/45 hover:text-white/75 transition-colors"
              data-testid="onboarding-skip-text"
            >
              Skip tour
            </button>
            <div className="flex items-center gap-2">
              {!isFirst && (
                <button
                  onClick={() => setI((n) => n - 1)}
                  className="glass-ghost h-8 px-3 rounded-lg flex items-center gap-1 text-xs font-medium text-white/85"
                  data-testid="onboarding-back"
                >
                  <ChevronLeft className="w-3.5 h-3.5" /> Back
                </button>
              )}
              <button
                onClick={() => (isLast ? finish(true) : setI((n) => n + 1))}
                className="gold-cta h-8 px-4 rounded-lg flex items-center gap-1 text-xs font-semibold"
                data-testid={isLast ? "onboarding-finish" : "onboarding-next"}
              >
                {isLast ? "Start costing" : (
                  <>
                    Next <ChevronRight className="w-3.5 h-3.5" />
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Arrow pointing at spotlight */}
          {rect && arrow && (
            <div
              className="absolute w-2.5 h-2.5 rotate-45"
              style={{
                background: "linear-gradient(180deg, rgba(24,20,14,0.92), rgba(14,12,9,0.94))",
                border: "1px solid rgba(232,207,154,0.35)",
                ...(arrow === "bottom" && { top: -6, left: "50%", marginLeft: -5, borderRight: "none", borderBottom: "none" }),
                ...(arrow === "top" && { bottom: -6, left: "50%", marginLeft: -5, borderLeft: "none", borderTop: "none" }),
                ...(arrow === "right" && { left: -6, top: "50%", marginTop: -5, borderRight: "none", borderTop: "none" }),
                ...(arrow === "left" && { right: -6, top: "50%", marginTop: -5, borderLeft: "none", borderBottom: "none" }),
              }}
            />
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );

  return createPortal(overlay, document.body);
}
